import {
  useEffect,
  useState,
  type ReactNode,
} from 'react'

import { useTranslation } from 'react-i18next'

import { Download, Search } from 'lucide-react'

import {
  notification,
} from '@/shared/components/notifications'

import { Button } from '../button'
import { InputGroup } from '../input-group'

const SEARCH_DEBOUNCE_MILLISECONDS = 300

export interface DataTableToolbarProps {
  showSearch: boolean
  searchValue: string
  onSearchChange: (value: string) => void

  toolbarContent?: ReactNode
  onExport?: () => void | Promise<void>
}

export function DataTableToolbar({
  showSearch,
  searchValue,
  onSearchChange,
  toolbarContent,
  onExport,
}: DataTableToolbarProps) {
  const { t } = useTranslation()

  const [inputValue, setInputValue] = useState(searchValue)

  const [isExporting, setIsExporting] = useState(false)

  /*
   * Follow external resets (for example a feature clearing the
   * query after navigation) by adjusting state during render
   * rather than in an effect, per
   * https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes.
   * This avoids an extra render pass and the
   * react-hooks/set-state-in-effect lint rule, which flags
   * setState calls made synchronously inside an effect body.
   */
  const [previousSearchValue, setPreviousSearchValue] =
    useState(searchValue)

  if (searchValue !== previousSearchValue) {
    setPreviousSearchValue(searchValue)
    setInputValue(searchValue)
  }

  useEffect(() => {
    if (inputValue === searchValue) {
      return
    }

    const handle = window.setTimeout(() => {
      onSearchChange(inputValue)
    }, SEARCH_DEBOUNCE_MILLISECONDS)

    return () => {
      window.clearTimeout(handle)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue])

  async function handleExport(): Promise<void> {
    if (!onExport || isExporting) {
      return
    }

    setIsExporting(true)

    try {
      await onExport()
    } catch {
      notification.error(
        t('ui.dataTable.exportFailed'),
      )
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div
      className={[
        'flex flex-col gap-3',
        'sm:flex-row sm:items-end',
        'sm:justify-between',
      ].join(' ')}
    >
      <div
        className={[
          'flex min-w-0 flex-1 flex-col gap-3',
          'sm:flex-row sm:items-end',
        ].join(' ')}
      >
        {showSearch ? (
          <InputGroup
            label={
              <span className="sr-only">
                {t('ui.dataTable.searchLabel')}
              </span>
            }
            type="search"
            placeholder={t(
              'ui.dataTable.searchPlaceholder',
            )}
            value={inputValue}
            onChange={setInputValue}
            leadingContent={
              <Search
                aria-hidden="true"
                className="h-4 w-4"
              />
            }
            className="w-full gap-0 sm:max-w-xs"
          />
        ) : null}

        {toolbarContent}
      </div>

      {onExport ? (
        <Button
          variant="secondary"
          isDisabled={isExporting}
          onPress={() => {
            void handleExport()
          }}
        >
          <Download
            aria-hidden="true"
            className="h-4 w-4"
          />

          {isExporting
            ? t('ui.dataTable.exporting')
            : t('ui.dataTable.export')}
        </Button>
      ) : null}
    </div>
  )
}
